import React, { useState } from 'react';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronDown } from 'lucide-react';

export function PerformanceFilter({ selected = [], customRange = null, onChange }) {
  const [showCustom, setShowCustom] = useState(false);
  const [localCustomRange, setLocalCustomRange] = useState(customRange || { min: 0, max: 100 });

  const presets = [
    { label: 'High (>70%)', value: 'high' },
    { label: 'Medium (40-70%)', value: 'medium' },
    { label: 'Low (<40%)', value: 'low' }
  ];

  const handlePresetChange = (presetValue, checked) => {
    const newPresets = checked
      ? [...selected, presetValue]
      : selected.filter(v => v !== presetValue);
    onChange({ presets: newPresets, customRange });
  };

  const handleCustomRangeChange = (newRange) => {
    setLocalCustomRange(newRange);
    onChange({ presets: selected, customRange: newRange });
  };

  const handleClearCustomRange = () => {
    setLocalCustomRange({ min: 0, max: 100 });
    onChange({ presets: selected, customRange: null });
  };

  return (
    <div className="flex flex-col gap-1 min-w-[200px]">
      <label className="text-sm font-medium text-gray-700">Performance</label>

      <div className="border rounded-md p-3 bg-white space-y-2">
        {/* Preset checkboxes */}
        {presets.map(preset => (
          <label key={preset.value} className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={selected.includes(preset.value)}
              onCheckedChange={(checked) => handlePresetChange(preset.value, checked)}
            />
            <span className="text-sm">{preset.label}</span>
          </label>
        ))}

        {/* Custom range toggle */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowCustom(!showCustom)}
          className="w-full justify-between mt-2"
        >
          <span className="text-xs">Custom Range</span>
          <ChevronDown className={`h-4 w-4 transition-transform ${showCustom ? 'rotate-180' : ''}`} />
        </Button>

        {/* Custom range sliders */}
        {showCustom && (
          <div className="space-y-3 pt-2 border-t">
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="text-xs text-gray-600">Min: {localCustomRange.min}%</label>
              </div>
              <Slider
                value={[localCustomRange.min]}
                onValueChange={([min]) => handleCustomRangeChange({ ...localCustomRange, min })}
                min={0}
                max={100}
                step={5}
                className="w-full"
              />
            </div>
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="text-xs text-gray-600">Max: {localCustomRange.max}%</label>
              </div>
              <Slider
                value={[localCustomRange.max]}
                onValueChange={([max]) => handleCustomRangeChange({ ...localCustomRange, max })}
                min={0}
                max={100}
                step={5}
                className="w-full"
              />
            </div>
            {customRange && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearCustomRange}
                className="w-full text-xs"
              >
                Clear Custom Range
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
